<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('schedule.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('schedule.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-4">
            <ts-button
              color="info"
              outline
              @click="$router.push({ name: 'schedule-create' })"
              >{{ $t('add_new') }}</ts-button
            >
          </div>
          <div class="col-sm-8 d-flex justify-content-end">
            <ts-input
              v-model="search"
              :placeholder="$t('Search')"
              style="width: 300px"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('invoice.filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table
        :columns="columns"
        :records="resources"
        :loading="loading"
        :rowGroup="{
          field: 'row_group_label'
        }"
      >
        <template v-slot="{ record }">
          <td style="white-space: nowrap">
            {{
              record.academic_class != null
                ? record.academic_class.branch != null
                  ? record.academic_class.branch.branch_name_en
                  : ''
                : ''
            }}
          </td>
          <td>
            {{
              record.academic_class != null
                ? record.academic_class.class_name
                : ''
            }}
          </td>
          <td>
            {{
              record.weekday_from != null ? record.weekday_from.wd_name_en : ''
            }}
          </td>
          <td>
            {{ record.weekday_to != null ? record.weekday_to.wd_name_en : '' }}
          </td>
          <td>
            {{
              record.schedule_type != null
                ? record.schedule_type.sch_type_name
                : ''
            }}
          </td>
          <td>
            {{
              record.academic_year != null ? record.academic_year.ac_year : ''
            }}
          </td>
          <td class="text-center">
            <a
              href="#"
              v-tooltip="$t('edit')"
              class="mr-2 text-primary"
              @click.prevent="edit(record)"
            >
              <i class="fas fa-edit"></i>
            </a>
            <a
              href="#"
              class="text-info ts-mr-2"
              @click.prevent="copySchedule(record)"
              v-tooltip="$t('copy')"
            >
              <i class="fas fa-copy"></i>
            </a>
            <a
              href="#"
              v-tooltip="$t('schedule.classSchedule')"
              class="text-primary"
              @click.prevent="schedule(record)"
            >
              <i class="fas fa-calendar-alt"></i>
            </a>
          </td>
        </template>
      </ts-table>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>

      <!-- drawer -->
      <a-drawer
        :title="$t('invoice.filter')"
        placement="right"
        :closable="false"
        :visible="visible"
        @close="() => (visible = false)"
        :zIndex="1020"
        width="300px"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="ts-text-xs">{{ $t('schedule.branch') }}</label>
              <a-select
                style="width: 100%"
                v-model="draft.branch_id"
                :options="branchs"
                showSearch
                mode="multiple"
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                :maxTagCount="1"
                :maxTagTextLength="17"
                :placeholder="$t('all')"
                @change="onDraftBranchChange"
              >
              </a-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="ts-text-xs">{{
                $t('schedule.academicYear')
              }}</label>
              <a-select
                style="width:100%"
                v-model="draft.ac_year_id"
                :options="acYearOptions"
                :show-search="true"
                :maxTagCount="1"
                mode="multiple"
                :placeholder="$t('all')"
                :filter-option="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                @change="onDraftAcYearChange"
              ></a-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="ts-text-xs">{{ $t('schedule.term') }}</label>
              <a-select
                style="width:100%"
                v-model="draft.ac_term_id"
                :options="termOptions"
                :show-search="true"
                :maxTagCount="1"
                mode="multiple"
                :placeholder="$t('all')"
                :filter-option="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                @change="onDraftTermChange"
              ></a-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="ts-text-xs">{{ $t('schedule.class') }}</label>
              <a-select
                style="width:100%"
                v-model="draft.class_id"
                :options="acClassOptions"
                :show-search="true"
                :maxTagCount="1"
                mode="multiple"
                :placeholder="$t('all')"
                :disabled="!draft.ac_term_id || draft.ac_term_id.length === 0"
                :filter-option="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
              ></a-select>
              <small
                class="text-muted"
                v-if="!draft.ac_term_id || draft.ac_term_id.length === 0"
              >
                <i>{{ $t('schedule.selectTermFirst') }}</i>
              </small>
            </div>
          </div>
        </div>

        <!-- Apply / Reset buttons -->
        <div class="row mt-3">
          <div class="col-md-6">
            <ts-button
              block
              color="secondary"
              outline
              @click.prevent="resetFilter"
            >
              {{ $t('reset') }}
            </ts-button>
          </div>
          <div class="col-md-6">
            <ts-button block color="primary" @click.prevent="applyFilter">
              {{ $t('apply') }}
            </ts-button>
          </div>
        </div>
      </a-drawer>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'schedule-table',
  data() {
    return {
      branchs: [],
      acYearOptions: [],
      termOptions: [],
      acClassOptions: [],
      loading: false,
      visible: false,

      draft: {
        branch_id: [],
        ac_year_id: [],
        ac_term_id: [],
        class_id: []
      },

      applied: {
        branch_id: [],
        ac_year_id: [],
        ac_term_id: [],
        class_id: []
      }
    }
  },
  mounted() {
    const defaultBranch = [this.$store.state.authUser.default_branch_id]
    const saved = this.$store.state.schedule.filterState

    if (saved && saved.initialized) {
      this.draft = {
        branch_id: saved.branch_id || [],
        ac_year_id: saved.ac_year_id || [],
        ac_term_id: saved.ac_term_id || [],
        class_id: saved.class_id || []
      }
      this.applied = { ...this.draft }
      this.restoreFilterOptions().then(() => {
        this.fetchData()
      })
    } else {
      this.draft.branch_id = defaultBranch
      this.applied.branch_id = defaultBranch
      this.initializeFilters()
    }
  },
  computed: {
    ...mapState('schedule', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.schedule.search
      },
      set(newValue) {
        this.$store.commit('schedule/SET_SEARCH', newValue)
        this.$store.commit('schedule/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('schedule.branch'),
          sortKey: 'academic_class.branch.branch_name_en',
          style: 'width: 10%'
        },
        {
          name: this.$t('schedule.className'),
          sortKey: 'academic_class.class_name'
        },
        {
          name: this.$t('schedule.dayFrom'),
          sortKey: 'academic_class.class_name'
        },
        { name: this.$t('schedule.dayTo'), sortKey: 'weekday_from.wd_name_en' },
        { name: this.$t('schedule.type'), sortKey: 'weekday_to.wd_name_en' },
        { name: this.$t('schedule.academicYear') },
        { name: this.$t('actions'), style: 'width: 1%; text-align: center' }
      ]
    }
  },
  methods: {
    ...mapActions('schedule', [
      'fetchClass',
      'fetchTerms',
      'fetchAcademicYear'
    ]),

    termLabel(el) {
      const name = el.term_name || ''
      if (el.ac_status_id === 1) return `${name} (Current)`
      if (el.ac_status_id === 2) return `${name} (History)`
      if (el.ac_status_id === 3) return `${name} (Next)`
      // ac_status_id missing or unknown — just show the name
      return name
    },

    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch(
          'schedule/fetch',
          Object.assign(
            {},
            {
              branch_id: this.applied.branch_id,
              ac_year_id: this.applied.ac_year_id,
              ac_term_id: this.applied.ac_term_id,
              class_id: this.applied.class_id
            },
            attributes
          )
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },

    // ─── First-visit initialization ──────────────────────────────────────────

    async initializeFilters() {
      try {
        await this.getBranch()
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    async getBranch() {
      try {
        const response = await this.$store.dispatch('user/getBranch', {
          filter: { user_branches: true }
        })
        this.branchs = response.data.map(el => ({
          value: el.branch_id,
          label: el.branch_name_en
        }))
        if (this.draft.branch_id && this.draft.branch_id.length) {
          await this.loadAcademicYears()
        } else {
          this.applyFilter()
        }
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    async loadAcademicYears() {
      try {
        const response = await this.fetchAcademicYear({
          branch_id: this.draft.branch_id
        })
        this.acYearOptions = response.data.map(el => ({
          value: el.ac_year_id,
          label: el.ac_year,
          ac_status_id: el.ac_status_id
        }))
        const currentYear = this.acYearOptions.find(y => y.ac_status_id === 1)
        if (currentYear) {
          this.draft.ac_year_id = [currentYear.value]
          await this.loadTermsForAcademicYear()
        } else {
          this.draft.ac_year_id = []
          this.termOptions = []
          this.draft.ac_term_id = []
          this.acClassOptions = []
          this.draft.class_id = []
          this.applyFilter()
        }
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    async loadTermsForAcademicYear() {
      if (!this.draft.ac_year_id || !this.draft.ac_year_id.length) {
        this.termOptions = []
        this.draft.ac_term_id = []
        this.acClassOptions = []
        this.draft.class_id = []
        this.applyFilter()
        return
      }
      try {
        const response = await this.fetchTerms({
          ac_year_id: this.draft.ac_year_id
        })
        this.termOptions = response.data.map(el => ({
          ac_status_id: el.ac_status_id,
          value: el.ac_term_id,
          label: this.termLabel(el)
        }))
        const justCopied = this.$route.query.copied === '1'
        if (!justCopied) {
          this.draft.ac_term_id = this.termOptions
            .filter(t => t.ac_status_id === 1)
            .map(t => t.value)
        } else {
          this.draft.ac_term_id = []
          this.$router.replace({ query: {} })
        }
        this.acClassOptions = []
        this.draft.class_id = []
        this.applyFilter()
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },

    // ─── Restore dropdown options when navigating back ────────────────────────

    async restoreFilterOptions() {
      try {
        const branchResponse = await this.$store.dispatch('user/getBranch', {
          filter: { user_branches: true }
        })
        this.branchs = branchResponse.data.map(el => ({
          value: el.branch_id,
          label: el.branch_name_en
        }))

        if (this.draft.branch_id && this.draft.branch_id.length) {
          const yearResponse = await this.fetchAcademicYear({
            branch_id: this.draft.branch_id
          })
          this.acYearOptions = yearResponse.data.map(el => ({
            value: el.ac_year_id,
            label: el.ac_year,
            ac_status_id: el.ac_status_id
          }))
        }

        if (this.draft.ac_year_id && this.draft.ac_year_id.length) {
          const termResponse = await this.fetchTerms({
            ac_year_id: this.draft.ac_year_id
          })
          this.termOptions = termResponse.data.map(el => ({
            ac_status_id: el.ac_status_id,
            value: el.ac_term_id,
            label: this.termLabel(el)
          }))
        }

        if (this.draft.ac_term_id && this.draft.ac_term_id.length) {
          const classResponse = await this.fetchClass({
            ac_term_id: this.draft.ac_term_id
          })
          this.acClassOptions = classResponse.data
            .map(el => ({
              value: el.class_id,
              label: el.academic_program_sub
                ? `${el.academic_program_sub.acps_name_foreign} - ${el.class_name}`
                : el.class_name,
              acps_id: el.acps_id
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },

    // ─── Draft change handlers — only load next dropdown, never fetch data ───

    async onDraftBranchChange() {
      this.acYearOptions = []
      this.draft.ac_year_id = []
      this.termOptions = []
      this.draft.ac_term_id = []
      this.acClassOptions = []
      this.draft.class_id = []

      if (!this.draft.branch_id || !this.draft.branch_id.length) return

      try {
        const response = await this.fetchAcademicYear({
          branch_id: this.draft.branch_id
        })
        this.acYearOptions = response.data.map(el => ({
          value: el.ac_year_id,
          label: el.ac_year,
          ac_status_id: el.ac_status_id
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    async onDraftAcYearChange() {
      this.termOptions = []
      this.draft.ac_term_id = []
      this.acClassOptions = []
      this.draft.class_id = []

      if (!this.draft.ac_year_id || !this.draft.ac_year_id.length) return

      try {
        const response = await this.fetchTerms({
          ac_year_id: this.draft.ac_year_id
        })
        this.termOptions = response.data.map(el => ({
          ac_status_id: el.ac_status_id,
          value: el.ac_term_id,
          label: this.termLabel(el)
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },
    async onDraftTermChange() {
      this.acClassOptions = []
      this.draft.class_id = []

      if (!this.draft.ac_term_id || !this.draft.ac_term_id.length) return

      try {
        const response = await this.fetchClass({
          ac_term_id: this.draft.ac_term_id
        })
        this.acClassOptions = response.data
          .map(el => ({
            value: el.class_id,
            label: el.academic_program_sub
              ? `${el.academic_program_sub.acps_name_foreign} - ${el.class_name}`
              : el.class_name,
            acps_id: el.acps_id
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },

    // ─── Apply / Reset ───────────────────────────────────────────────────────

    applyFilter() {
      this.applied = { ...this.draft }
      this.$store.commit('schedule/SET_FILTER_STATE', {
        ...this.draft,
        initialized: true
      })
      this.visible = false
      this.$store.commit('schedule/RESET_CURRENT_PAGE')
      this.fetchData()
    },
    resetFilter() {
      const defaultBranch = [this.$store.state.authUser.default_branch_id]
      this.draft = {
        branch_id: defaultBranch,
        ac_year_id: [],
        ac_term_id: [],
        class_id: []
      }
      this.acYearOptions = []
      this.termOptions = []
      this.acClassOptions = []
      this.$store.commit('schedule/SET_FILTER_STATE', { initialized: false })
      this.initializeFilters()
    },

    // ─── Row actions ─────────────────────────────────────────────────────────

    edit(record) {
      this.$store.commit('schedule/SET_FOUND_DATA', record)
      this.$router.push({
        name: 'schedule-edit',
        params: { id: record.sch_id }
      })
    },
    schedule(record) {
      this.$store.commit('schedule/SET_FOUND_DATA', record)
      this.$router.push({
        name: 'schedule-show',
        params: { id: record.sch_id }
      })
    },
    async copySchedule(record) {
      this.$store.commit('schedule/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'schedule-copy-schedule',
        params: {
          sch_id: record.sch_id,
          class_name: record.academic_class.class_name
        }
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500)
  }
}
</script>
